import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { styled } from '../../../../../shared/Theme/theme';
import {
    bpAccountingFilters,
    bpAccountingRange,
    bpAccountingResponse,
    bpAccountingRow,
    bpAccountingStatusFilter
} from '../../../../../../models/entities/businessPartner/accounting';
import { businessPartner, settings as bpSettings } from '../../../../../../models/entities/businessPartner/businessPartner';
import { settings as shipmentSettings } from '../../../../../../models/entities/shipment/shipment';
import { company } from '../../../../../../models/entities/company/company';
import { localSDK as client } from '../../../../../../sdk';
import KpiCards from './kpiCards';
import StatusPill from './statusPill';
import AddPaymentModal from './AddPaymentModal';
import SendInvoiceModal from './SendInvoiceModal';
import DownloadInvoiceModal from './DownloadInvoiceModal';
import CustomModal, { ModalTriggerProps } from '../../../../../shared/Modal/Modal';
import Loader from '../../../../../shared/SmallComponents/Loader';
import {
    SearchOutlined,
    DownloadOutlined,
    PlusOutlined,
    FilePdfOutlined,
    LeftOutlined,
    RightOutlined,
    MailOutlined
} from '@ant-design/icons';
import { formatDate, formatMoney } from './format';
import { DropDown, TextInput } from '../../../../../shared/inputs/base';
import { ACCOUNTING_STATE_CATALOG, lookupAccountingState } from './accountingStateCatalog';
import { useProfitInBase, ProfitInBaseState } from './useProfitInBase';
import { useCostInBase, CostInBaseState } from './useCostInBase';
import {
    buildBpInvoiceIssuer
} from './invoicePdfTypes';
import { filterAccountingRowsBySearch } from './filterAccountingRowsBySearch';
import { localizationSelectors } from '../../../../../../state/ducks/localization/selectors';
import { singleCompanySelectors } from '../../../../../../state/ducks/singleCompany/selectors';
import { shipmentsSelectors } from '../../../../../../state/ducks/shipments/selectors';
import { RootState } from '../../../../../../state/store/store';

type Props = {
    businessPartner: businessPartner;
    settings?: bpSettings;
    issuerCompany?: company;
    shipmentSettings?: shipmentSettings;
    accountingLocalization?: {
        send_invoice: string;
        download_invoice: string;
        column_aging: string;
        send_modal: {
            title: string;
            contacts_placeholder: string;
            send_btn: string;
            includes_documents: string;
            no_financial_contacts: string;
            sending: string;
            success: string;
            error: string;
        };
    };
};

const RANGE_OPTIONS: Array<{ value: bpAccountingRange; text: string }> = [
    { value: 'all', text: 'All Time' },
    { value: '30d', text: 'Last 30 Days' },
    { value: '60d', text: 'Last 60 Days' },
    { value: '90d', text: 'Last 90 Days' },
    { value: '180d', text: 'Last 6 Months' },
    { value: '1y', text: 'Last 1 Year' }
];

const STATUS_OPTIONS: Array<{ value: bpAccountingStatusFilter; text: string }> = [
    { value: 'ALL', text: 'All Statuses' },
    ...[...ACCOUNTING_STATE_CATALOG]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((s) => ({
            value: s.code,
            text: s.name
        })),
    { value: 'NONE', text: 'No Status' }
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((s) => ({ value: String(s), text: String(s) }));

const downloadCsv = (
    rows: bpAccountingRow[],
    costInBase: Record<string, CostInBaseState>,
    profitInBase: Record<string, ProfitInBaseState>,
    baseCurrency: string
) => {
    const header = [
        'Shipment #',
        'Shipment Date',
        `Total Cost (${baseCurrency})`,
        `Total Profit (${baseCurrency})`,
        'Currency',
        'Paid',
        'Open Balance',
        'Profit',
        'Due Date',
        'Aging (days)',
        'Accounting Status'
    ];
    const body = rows.map((r) => {
        const pib = profitInBase[r.shipmentId];
        const pibValue =
            pib && (pib.status === 'ok' || pib.status === 'partial') ? pib.total.toFixed(2) : '';
        const cib = costInBase[r.shipmentId];
        const cibValue = cib?.status === 'ok' ? cib.total.toFixed(2) : '';
        const stateLabel = lookupAccountingState(r.accountingState)?.name ?? r.accountingState ?? '';
        return [
            r.shipmentNumber,
            formatDate(r.shipmentDate),
            cibValue,
            pibValue,
            r.currency,
            r.paid.toFixed(2),
            r.openBalance.toFixed(2),
            r.profit.toFixed(2),
            formatDate(r.dueDate),
            r.agingDays != null ? String(r.agingDays) : '',
            stateLabel
        ];
    });
    const csv = [header, ...body].map((line) => line.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounting-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const AccountingTab = ({
    businessPartner,
    issuerCompany,
    shipmentSettings,
    accountingLocalization
}: Props) => {
    const businessPartnerId = businessPartner.id;
    const [filters, setFilters] = useState<bpAccountingFilters>({ range: 'all', status: 'ALL' });
    const [searchInput, setSearchInput] = useState('');
    const [gridSearch, setGridSearch] = useState('');
    const [isSearchDebouncing, setIsSearchDebouncing] = useState(false);
    const [data, setData] = useState<bpAccountingResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [addPaymentOpen, setAddPaymentOpen] = useState(false);
    const [sendInvoiceRow, setSendInvoiceRow] = useState<bpAccountingRow | null>(null);
    const [downloadInvoiceRow, setDownloadInvoiceRow] = useState<bpAccountingRow | null>(null);

    const issuer = useMemo(() => buildBpInvoiceIssuer(issuerCompany), [issuerCompany]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await client.businessPartners().accounting().fetchAccounting(businessPartnerId, filters);
            setData(result);
            setPage(1);
        } catch (e) {
            setError((e as Error)?.message || 'Failed to load accounting');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessPartnerId, filters.range, filters.status]);

    useEffect(() => {
        setIsSearchDebouncing(true);
        const timeout = setTimeout(() => {
            setGridSearch(searchInput.trim());
            setIsSearchDebouncing(false);
            setPage(1);
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchInput]);

    const baseCurrency = data?.kpis.currency || 'USD';
    const allRows = data?.rows ?? [];
    const costInBase = useCostInBase(allRows, baseCurrency);
    const profitInBase = useProfitInBase(allRows, baseCurrency);

    const rows = useMemo(
        () => filterAccountingRowsBySearch(allRows, gridSearch, costInBase, profitInBase, baseCurrency),
        [allRows, gridSearch, costInBase, profitInBase, baseCurrency]
    );

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const pagedRows = useMemo(() => rows.slice((page - 1) * pageSize, page * pageSize), [rows, page, pageSize]);
    const cellMoney = (v: number, cur: string) => formatMoney(v, cur, { decimals: 'auto' });
    const profitColor = (v: number) => (v > 0 ? '#15803d' : v < 0 ? '#b91c1c' : '#1f2937');

    const handleAddPayment = async (input: {
        shipmentId: string;
        amount: number;
        currency: string;
        paidAt: string;
    }) => {
        await client.businessPartners().accounting().addPayment(businessPartnerId, input);
        setAddPaymentOpen(false);
        await fetchData();
    };

    const handleExportPdf = () => window.print();

    const loc = accountingLocalization;
    const tableColCount = loc ? 11 : 10;

    return (
        <Wrapper>
            <SectionHeader>
                <HeaderTitleBlock>
                    <SectionTitle>Balance Summary</SectionTitle>
                    {data?.kpis?.currency && (
                        <SectionSubtle>All amounts in {data.kpis.currency}</SectionSubtle>
                    )}
                </HeaderTitleBlock>
                <HeaderActions>
                    <ActionButton onClick={handleExportPdf} disabled={!rows.length}>
                        <FilePdfOutlined /> Export PDF
                    </ActionButton>
                    <CustomModal
                        isOpen={addPaymentOpen}
                        title={{ text: 'Add Payment' }}
                        Trigger={(props: ModalTriggerProps) => (
                            <PrimaryButton
                                onClick={() => {
                                    setAddPaymentOpen(true);
                                    props.onClick?.();
                                }}
                                disabled={!rows.length}
                            >
                                <PlusOutlined /> Add Payment
                            </PrimaryButton>
                        )}
                        Content={() => (
                            <AddPaymentModal
                                rows={allRows}
                                onClose={() => setAddPaymentOpen(false)}
                                onSubmit={handleAddPayment}
                            />
                        )}
                        width="420px"
                        onCancelCallback={() => setAddPaymentOpen(false)}
                    />
                </HeaderActions>
            </SectionHeader>

            {data && <KpiCards kpis={data.kpis} />}

            <FilterBar>
                <FilterField $width="280px">
                    <DropDown
                        options={STATUS_OPTIONS}
                        value={filters.status}
                        onChange={(v: bpAccountingStatusFilter) => setFilters({ ...filters, status: v })}
                        placeHolder="All Statuses"
                        showSearch
                        width="100%"
                    />
                </FilterField>
                <FilterField $width="180px">
                    <DropDown
                        options={RANGE_OPTIONS}
                        value={filters.range}
                        onChange={(v: bpAccountingRange) => setFilters({ ...filters, range: v })}
                        placeHolder="All Time"
                        width="100%"
                    />
                </FilterField>
                <FilterField $flex={1}>
                    <TextInput
                        value={searchInput}
                        placeHolder="Search..."
                        onChange={(v: string) => setSearchInput(v)}
                        suffix={
                            isSearchDebouncing ? (
                                <Loader width="15px" showText={false} marginTop="0" />
                            ) : (
                                <SearchOutlined style={{ color: '#9ca3af' }} />
                            )
                        }
                        allowClear
                    />
                </FilterField>
                <ActionButton onClick={() => downloadCsv(rows, costInBase, profitInBase, baseCurrency)} disabled={!rows.length}>
                    <DownloadOutlined /> Download CSV
                </ActionButton>
            </FilterBar>

            {loading && (
                <Center>
                    <Loader width="36px" />
                </Center>
            )}
            {error && <ErrorBanner>{error}</ErrorBanner>}

            {!loading && !error && (
                <TableCard>
                    <Table>
                        <thead>
                            <tr>
                                <Th>Shipment #</Th>
                                <Th>Shipment Date</Th>
                                <Th $right>Total Cost ({baseCurrency})</Th>
                                <Th $right>Total Profit ({baseCurrency})</Th>
                                <Th $right>Paid</Th>
                                <Th $right>Open Balance</Th>
                                <Th $right>Profit</Th>
                                <Th>Due Date</Th>
                                <Th $right>{loc?.column_aging ?? 'Aging'}</Th>
                                <Th>Status</Th>
                                {loc && <Th $center>Actions</Th>}
                            </tr>
                        </thead>
                        <tbody>
                            {pagedRows.length === 0 && (
                                <tr>
                                    <EmptyTd colSpan={tableColCount}>No shipments to show for the selected filters.</EmptyTd>
                                </tr>
                            )}
                            {pagedRows.map((r) => {
                                const pib = profitInBase[r.shipmentId];
                                const cib = costInBase[r.shipmentId];
                                return (
                                    <Row key={r.shipmentId}>
                                        <Td>
                                            <ShipmentLink href={`/shipments/${r.shipmentId}`}>
                                                {r.shipmentNumber}
                                            </ShipmentLink>
                                        </Td>
                                        <Td>{formatDate(r.shipmentDate)}</Td>
                                        <Td $right>
                                            <CostBaseCell state={cib} baseCurrency={baseCurrency} />
                                        </Td>
                                        <Td $right>
                                            <ProfitBaseCell state={pib} baseCurrency={baseCurrency} />
                                        </Td>
                                        <Td $right>{cellMoney(r.paid, r.currency)}</Td>
                                        <Td $right>
                                            <strong>{cellMoney(r.openBalance, r.currency)}</strong>
                                        </Td>
                                        <Td $right>
                                            <span style={{ color: profitColor(r.profit), fontWeight: 600 }}>
                                                {cellMoney(r.profit, r.currency)}
                                            </span>
                                        </Td>
                                        <Td>{formatDate(r.dueDate)}</Td>
                                        <Td $right>
                                            {r.agingDays != null ? (
                                                <span title="Days since invoice date">{r.agingDays}</span>
                                            ) : (
                                                <CellMuted>—</CellMuted>
                                            )}
                                        </Td>
                                        <Td>
                                            <StatusPill code={r.accountingState} />
                                        </Td>
                                        {loc && (
                                            <Td $center>
                                                <RowActions>
                                                    <RowActionBtn
                                                        type="button"
                                                        title={loc.download_invoice}
                                                        onClick={() => setDownloadInvoiceRow(r)}
                                                    >
                                                        <FilePdfOutlined />
                                                    </RowActionBtn>
                                                    <RowActionBtn
                                                        type="button"
                                                        title={loc.send_invoice}
                                                        onClick={() => setSendInvoiceRow(r)}
                                                    >
                                                        <MailOutlined />
                                                    </RowActionBtn>
                                                </RowActions>
                                            </Td>
                                        )}
                                    </Row>
                                );
                            })}
                        </tbody>
                    </Table>
                    <Pager>
                        <PagerLabel>Rows per page:</PagerLabel>
                        <PagerSelectWrap>
                            <DropDown
                                options={PAGE_SIZE_OPTIONS}
                                value={String(pageSize)}
                                onChange={(v: string) => {
                                    setPageSize(Number(v));
                                    setPage(1);
                                }}
                                width="76px"
                            />
                        </PagerSelectWrap>
                        <PagerInfo>
                            {rows.length === 0
                                ? '0-0 of 0'
                                : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, rows.length)} of ${rows.length}`}
                        </PagerInfo>
                        <PagerBtn onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>
                            <LeftOutlined />
                        </PagerBtn>
                        <PagerBtn onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>
                            <RightOutlined />
                        </PagerBtn>
                    </Pager>
                </TableCard>
            )}

            {sendInvoiceRow && data && loc && (
                <CustomModal
                    isOpen={true}
                    title={{ text: loc.send_modal.title }}
                    width="480px"
                    onCancelCallback={() => setSendInvoiceRow(null)}
                    Content={() => (
                        <SendInvoiceModal
                            businessPartner={businessPartner}
                            row={sendInvoiceRow}
                            accountingData={data}
                            issuer={issuer}
                            shipmentSettings={shipmentSettings}
                            localization={{ send_modal: loc.send_modal }}
                            onClose={() => setSendInvoiceRow(null)}
                        />
                    )}
                />
            )}
            {downloadInvoiceRow && data && (
                <CustomModal
                    isOpen={true}
                    title={{ text: loc?.download_invoice || 'Download invoice' }}
                    width="480px"
                    onCancelCallback={() => setDownloadInvoiceRow(null)}
                    Content={() => (
                        <DownloadInvoiceModal
                            businessPartner={businessPartner}
                            row={downloadInvoiceRow}
                            accountingData={data}
                            issuer={issuer}
                            shipmentSettings={shipmentSettings}
                            onClose={() => setDownloadInvoiceRow(null)}
                        />
                    )}
                />
            )}
        </Wrapper>
    );
};

type CostBaseCellProps = { state: CostInBaseState | undefined; baseCurrency: string };
const CostBaseCell = ({ state, baseCurrency }: CostBaseCellProps) => {
    if (!state || state.status === 'loading') return <Loader width="14px" />;
    if (state.status === 'no_base') return <CellMuted>—</CellMuted>;
    if (state.status === 'error') return <CellError title="Could not load FX rates">FX error</CellError>;
    return <span>{formatMoney(state.total, baseCurrency, { decimals: 'auto' })}</span>;
};

type ProfitBaseCellProps = { state: ProfitInBaseState | undefined; baseCurrency: string };
const ProfitBaseCell = ({ state, baseCurrency }: ProfitBaseCellProps) => {
    if (!state || state.status === 'loading') return <Loader width="14px" />;
    if (state.status === 'no_base') return <CellMuted>—</CellMuted>;
    if (state.status === 'error') return <CellError title="Could not load FX rates">FX error</CellError>;
    const color = state.total > 0 ? '#15803d' : state.total < 0 ? '#b91c1c' : '#1f2937';
    return (
        <span
            style={{ color, fontWeight: 600 }}
            title={
                state.status === 'partial'
                    ? `Excludes profit in: ${state.missingCurrencies.join(', ')}`
                    : undefined
            }
        >
            {formatMoney(state.total, baseCurrency, { decimals: 'auto' })}
            {state.status === 'partial' ? <PartialMark>*</PartialMark> : null}
        </span>
    );
};

const Wrapper = styled.div`
    width: 100%;
    padding: 4px 0 24px;
    color: #1f2937;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
`;

const HeaderTitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const SectionSubtle = styled.div`
    font-size: 12px;
    color: #6b7280;
    font-weight: 400;
`;

const SectionTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.sectionTitle};
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 10px;
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 14px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid #d8dde3;
    color: #344054;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 120ms ease, border-color 120ms ease;
    &:hover:not(:disabled) {
        background: #f7f9fc;
        border-color: #c2c8d0;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PrimaryButton = styled(ActionButton)`
    background: ${(p) => p.theme.colors.primaryBlue};
    border-color: ${(p) => p.theme.colors.primaryBlue};
    color: #fff;
    box-shadow: 0 1px 2px rgba(0, 138, 201, 0.16);
    &:hover:not(:disabled) {
        background: #0079b3;
        border-color: #0079b3;
    }
`;

const FilterBar = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
    align-items: center;
`;

const FilterField = styled.div<{ $width?: string; $flex?: number }>`
    width: ${(p) => p.$width ?? 'auto'};
    flex: ${(p) => (p.$flex ? p.$flex : '0 0 auto')};
    min-width: 0;
`;

const CellMuted = styled.span`
    color: #9ca3af;
`;

const CellError = styled.span`
    color: #b91c1c;
    font-size: 12px;
    cursor: help;
`;

const PartialMark = styled.span`
    color: #b45309;
    margin-left: 2px;
    font-weight: 700;
`;

const TableCard = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
`;

const Th = styled.th<{ $right?: boolean; $center?: boolean }>`
    text-align: ${(p) => (p.$right ? 'right' : p.$center ? 'center' : 'left')};
    padding: 12px 16px;
    background: #fafbfc;
    color: #6b7280;
    font-weight: 500;
    font-size: 12px;
    border-bottom: 1px solid #eef0f3;
    letter-spacing: 0.1px;
`;

const Row = styled.tr`
    &:hover td {
        background: #fafcfe;
    }
`;

const Td = styled.td<{ $right?: boolean; $center?: boolean }>`
    text-align: ${(p) => (p.$right ? 'right' : p.$center ? 'center' : 'left')};
    padding: 13px 16px;
    border-bottom: 1px solid #f1f3f5;
    color: #1f2937;
    vertical-align: middle;
`;

const EmptyTd = styled.td`
    text-align: center;
    padding: 40px;
    color: #6b7280;
`;

const ShipmentLink = styled.a`
    color: ${(p) => p.theme.colors.primaryBlue};
    cursor: pointer;
    font-weight: 500;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const Pager = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    background: #fafbfc;
    font-size: 12px;
    color: #6b7280;
    border-top: 1px solid #eef0f3;
`;

const PagerLabel = styled.span``;

const PagerSelectWrap = styled.div`
    width: 76px;
`;

const PagerInfo = styled.span`
    margin: 0 8px;
`;

const PagerBtn = styled.button`
    background: #fff;
    border: 1px solid #d8dde3;
    border-radius: 6px;
    width: 28px;
    height: 26px;
    cursor: pointer;
    color: #4b5563;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

const Center = styled.div`
    display: flex;
    justify-content: center;
    padding: 36px 0;
`;

const ErrorBanner = styled.div`
    background: #fde7e7;
    color: #b32424;
    padding: 10px 12px;
    border-radius: 6px;
    margin-bottom: 12px;
    font-size: 13px;
`;

const RowActions = styled.div`
    display: inline-flex;
    gap: 4px;
    align-items: center;
    justify-content: center;
`;

const RowActionBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 26px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    color: #4b5563;
    cursor: pointer;
    &:hover:not(:disabled) {
        border-color: #008ac9;
        color: #008ac9;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const mapStateToProps = (state: RootState) => {
    const pageLoc = localizationSelectors.SingleBusinessPartnerPage(state);
    return {
        issuerCompany: singleCompanySelectors.company(state),
        shipmentSettings: shipmentsSelectors.settingsByLanguage(state)(),
        accountingLocalization: pageLoc?.accounting
    };
};

export default connect(mapStateToProps)(AccountingTab);
